import { useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useState } from "react";

import {
  ADMIN_SELECTION_USER_IDS,
  BULK_INVITE_WITH_USER_IDS,
  VALIDATE_FOR_SELECTION_BULK_INVITE,
} from "../gql/mutations/event";
import { RECORD_INVITES } from "../gql/mutations/outreach";
import { SHARE_EVENT_TO_USER_IDS } from "../gql/queries/shareEvent";
import { FADER_LABELS, ZONE_LABELS } from "../helpers/const";
import { tzLabel, tzOffsetMinutes, UTC_TZ } from "../helpers/timezones";
import type { FaderLevel, ZoneLabel } from "../types/graphql";

/**
 * "Invite / Share this zone" for one event, straight from the calendar.
 *
 * When a timezone is picked in the calendar's "Show times in" dropdown, this
 * targets **everyone on that clock** (members at the same UTC offset) — so you
 * can invite, say, the UK evening crowd to an event that lands well for them.
 * With no timezone picked it falls back to the event's colour-zone row. Either
 * way the cohort narrows by whatever member type / plan is being previewed, plus
 * an optional risk filter.
 *
 * Invite and Share differ on capacity: an **invite reserves a seat**, so a full
 * event is refused (that limit is shown inline). A **share is only a heads-up**
 * — it reserves nothing, whoever opens it first takes the spot — so it resolves
 * its audience capacity-free (adminSelectionUserIds) and always works. Nothing
 * sends until you confirm; invites log to the outreach board, shares log
 * themselves server-side.
 */

/** Mirrors MemberExperienceBar's ALL_MEMBERS — the "don't filter" sentinel. */
const ALL_MEMBERS = "ALL";

type Action = "invite" | "share";

interface Preview {
  eventTitle: string;
  userIds: string[];
  warnings: string[];
  /** Share only: what the server's dry run said about this audience. */
  skipped?: { alreadyShared: number; notAllowed: number; inactive: number };
  /** Share only: set when the filters matched more members than one run carries. */
  truncated?: { totalMatched: number; cap: number };
}

interface Props {
  eventId: string;
  eventTitle: string;
  /** The colour-zone row the event sits in — the target when no timezone is picked. */
  zone: ZoneLabel;
  /** The selected display timezone; when set (not UTC) the invite targets everyone on that clock. */
  tz: string;
  /** The previewed member type (profile.kind), or undefined for "all members". */
  cohortKind?: string;
  /** The previewed plan (profile.planSlug), or undefined for "all plans". */
  cohortPlan?: string;
}

const CalendarZoneOutreach: React.FC<Props> = ({
  eventId,
  eventTitle,
  zone,
  tz,
  cohortKind,
  cohortPlan,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [action, setAction] = useState<Action | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [faders, setFaders] = useState<FaderLevel[]>([]);

  const [validate, { loading: validating }] = useMutation(VALIDATE_FOR_SELECTION_BULK_INVITE);
  const [selectIds, { loading: selecting }] = useMutation(ADMIN_SELECTION_USER_IDS);
  const [invite, { loading: inviting }] = useMutation(BULK_INVITE_WITH_USER_IDS);
  const [recordCohort] = useMutation(RECORD_INVITES);
  const [share, { loading: sharing }] = useMutation(SHARE_EVENT_TO_USER_IDS);

  const byTimezone = Boolean(tz && tz !== UTC_TZ);
  const hasKind = Boolean(cohortKind && cohortKind !== ALL_MEMBERS);
  const hasPlan = Boolean(cohortPlan && cohortPlan !== ALL_MEMBERS);

  const filters = {
    ...(byTimezone ? { timezoneOffsetMinutes: tzOffsetMinutes(tz) } : { zones: [zone] }),
    kind: hasKind ? [cohortKind] : undefined,
    plan: hasPlan ? [cohortPlan] : undefined,
    faderLevels: faders.length ? faders : undefined,
    all: true,
  };

  const groupLabel = byTimezone ? tzLabel(tz) : (ZONE_LABELS.get(zone) ?? zone);
  const narrowed = hasKind || hasPlan || faders.length > 0;
  const loading = validating || selecting;
  const busy = loading || inviting || sharing;

  const startPreview = async (next: Action) => {
    setAction(next);
    setPreview(null);
    setError(null);
    try {
      if (next === "share") {
        // Share is capacity-free — a full event is still shareable. Passing the
        // event means the server runs its own share dry run, so the number below
        // is who will actually receive it rather than who matched the filters.
        const { data } = await selectIds({ variables: { filters, eventId } });
        const selection = data?.adminSelectionUserIds;
        setPreview({
          eventTitle,
          userIds: selection?.userIds ?? [],
          warnings: [],
          skipped: {
            alreadyShared: selection?.alreadySharedCount ?? 0,
            notAllowed: selection?.notAllowedCount ?? 0,
            inactive: selection?.inactiveCount ?? 0,
          },
          truncated: selection?.truncated
            ? { totalMatched: selection.totalMatched, cap: selection.cap }
            : undefined,
        });
      } else {
        const { data } = await validate({ variables: { eventId, filters } });
        const result = data?.validateForSelectionBulkInvite;
        setPreview({
          eventTitle: result?.eventTitle ?? eventTitle,
          userIds: result?.userIds ?? [],
          warnings: result?.warningMessages ?? [],
        });
      }
    } catch (caught) {
      // Keep the panel open and show the reason inline (the invite capacity
      // limit lands here) rather than firing a blunt error toast.
      setError(caught instanceof Error ? caught.message : "Something went wrong.");
    }
  };

  const cancel = () => {
    setAction(null);
    setPreview(null);
    setError(null);
  };

  const send = async () => {
    if (!preview || preview.userIds.length === 0) {
      return;
    }
    try {
      if (action === "invite") {
        await invite({ variables: { eventId, input: { userIds: preview.userIds } } });
        try {
          await recordCohort({
            variables: {
              eventId,
              userIds: preview.userIds,
              source: "ZONE_OUTREACH",
              filters: [`Zone: ${groupLabel}`],
            },
          });
        } catch {
          // The invite succeeded regardless — the log is a record, not a gate.
        }
        enqueueSnackbar(`Invited ${preview.userIds.length} member(s) in ${groupLabel}.`, {
          variant: "success",
        });
      } else {
        const { data } = await share({
          variables: { eventId, userIds: preview.userIds, source: "ZONE_OUTREACH" },
        });
        const sharedCount = data?.adminShareEventToUserIds?.sharedCount ?? preview.userIds.length;
        enqueueSnackbar(`Shared with ${sharedCount} member(s) in ${groupLabel}.`, {
          variant: "success",
        });
      }
      cancel();
    } catch (caught) {
      enqueueSnackbar(caught instanceof Error ? caught.message : "Send failed.", {
        variant: "error",
      });
    }
  };

  if (!action) {
    return (
      <Box display="flex" gap={0.75} alignItems="center" flexWrap="wrap" sx={{ width: "100%" }}>
        <Button size="small" variant="outlined" onClick={() => startPreview("invite")}>
          Invite {byTimezone ? groupLabel : "zone"}
        </Button>
        <Button size="small" variant="outlined" onClick={() => startPreview("share")}>
          Share to {byTimezone ? groupLabel : "zone"}
        </Button>
        <TextField
          select
          size="small"
          label="Risk (optional)"
          value={faders}
          SelectProps={{
            multiple: true,
            renderValue: (selected) =>
              (selected as FaderLevel[])
                .map((fader) => FADER_LABELS.get(fader) ?? fader)
                .join(", "),
          }}
          onChange={(event) => setFaders(event.target.value as unknown as FaderLevel[])}
          sx={{ minWidth: 170 }}
        >
          {[...FADER_LABELS.keys()].map((fader) => (
            <MenuItem key={fader} value={fader}>
              <Checkbox checked={faders.includes(fader)} size="small" />
              {FADER_LABELS.get(fader)}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    );
  }

  const verb = action === "invite" ? "Invite" : "Share";
  const ready = !error && preview && preview.userIds.length > 0;
  // The event-is-full / nearly-full case — steer her to Share, which doesn't
  // reserve a seat, so everyone still hears about it.
  const isCapacity = Boolean(error && /invite only|max number of participants/i.test(error));

  return (
    <Alert
      severity={error ? "warning" : preview && preview.userIds.length === 0 ? "warning" : "info"}
      sx={{ width: "100%", mt: 0.5 }}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" color="inherit" onClick={cancel} disabled={busy}>
            Cancel
          </Button>
          {!error && (
            <Button size="small" variant="contained" onClick={send} disabled={busy || !ready}>
              {busy ? "Working…" : `${verb} ${preview?.userIds.length ?? ""}`}
            </Button>
          )}
          {isCapacity && (
            <Button size="small" variant="contained" onClick={() => startPreview("share")}>
              Share instead
            </Button>
          )}
        </Stack>
      }
    >
      {error ? (
        <Typography variant="body2">
          {error}
          {isCapacity && (
            <Box component="span" sx={{ color: "text.secondary" }}>
              {" "}
              An invite reserves a seat. <strong>Share</strong> instead to reach everyone in{" "}
              {groupLabel} — it doesn’t reserve a seat, so whoever opens it first takes the spot.
            </Box>
          )}
        </Typography>
      ) : loading || !preview ? (
        <Typography variant="body2">Checking who’s in {groupLabel}…</Typography>
      ) : (
        <>
          <Typography variant="body2">
            <strong>{preview.userIds.length}</strong> member
            {preview.userIds.length === 1 ? "" : "s"} in <strong>{groupLabel}</strong>
            {narrowed ? " matching your filters" : ""} would be{" "}
            {action === "invite" ? "invited to" : "sent"} <strong>{preview.eventTitle}</strong>.
          </Typography>
          {preview.truncated && (
            <Typography variant="body2" mt={0.5}>
              {preview.truncated.totalMatched} members matched — this run carries the first{" "}
              {preview.truncated.cap}. Narrow the filters, or send it in a few passes.
            </Typography>
          )}
          {preview.skipped &&
            preview.skipped.alreadyShared + preview.skipped.notAllowed + preview.skipped.inactive >
              0 && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Not counted:{" "}
                {[
                  preview.skipped.alreadyShared && `${preview.skipped.alreadyShared} already sent`,
                  preview.skipped.notAllowed && `${preview.skipped.notAllowed} can't attend`,
                  preview.skipped.inactive && `${preview.skipped.inactive} inactive`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                .
              </Typography>
            )}
          {preview.warnings.length > 0 && (
            <Box mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
              {preview.warnings.map((warning) => (
                <Chip key={warning} label={warning} size="small" color="warning" />
              ))}
            </Box>
          )}
        </>
      )}
    </Alert>
  );
};

export default CalendarZoneOutreach;
